// services/FirstTimeUserService.js

class FirstTimeUserService {
  constructor() {
    this.storageKey = 'preptune-first-time-user';
    this.guideCompletedKey = 'preptune-guide-completed';
    this.firstCallKey = 'preptune-first-call-completed';
  }

  /**
   * Check if this is a first-time user
   * @returns {boolean}
   */
  isFirstTimeUser() {
    try {
      const hasCompletedGuide = localStorage.getItem(this.guideCompletedKey);
      const hasCompletedFirstCall = localStorage.getItem(this.firstCallKey);
      
      // User is considered first-time if they haven't completed the guide AND first call
      return !hasCompletedGuide || !hasCompletedFirstCall;
    } catch (error) {
      console.error('Error checking first-time user status:', error);
      return true; // Default to showing guide if we can't determine status
    }
  }

  /**
   * Check if user has completed the initial guide
   * @returns {boolean}
   */
  hasCompletedGuide() {
    try {
      return localStorage.getItem(this.guideCompletedKey) === 'true';
    } catch (error) {
      console.error('Error checking guide completion status:', error);
      return false;
    }
  }

  /**
   * Check if user should see the guide (after voice selection)
   * @returns {boolean}
   */
  shouldShowGuide() {
    try {
      // Show guide if user hasn't completed it yet
      return !this.hasCompletedGuide();
    } catch (error) {
      console.error('Error determining if guide should show:', error);
      return true;
    }
  }

  /**
   * Mark the guide as completed
   */
  markGuideCompleted() {
    try {
      localStorage.setItem(this.guideCompletedKey, 'true');
      localStorage.setItem(this.storageKey, 'false'); // No longer first-time for guide
      console.log('✅ First-time user guide marked as completed');
    } catch (error) {
      console.error('Error marking guide as completed:', error);
    }
  }

  /**
   * Mark the first call as completed
   */
  markFirstCallCompleted() {
    try {
      localStorage.setItem(this.firstCallKey, 'true');
      console.log('✅ First call marked as completed');
    } catch (error) {
      console.error('Error marking first call as completed:', error);
    }
  }

  /**
   * Check if user has completed their first call
   * @returns {boolean}
   */
  hasCompletedFirstCall() {
    try {
      return localStorage.getItem(this.firstCallKey) === 'true';
    } catch (error) {
      console.error('Error checking first call status:', error);
      return false;
    }
  }

  /**
   * Get user onboarding progress
   * @returns {Object}
   */
  getOnboardingProgress() {
    return {
      isFirstTime: this.isFirstTimeUser(),
      hasCompletedGuide: this.hasCompletedGuide(),
      hasCompletedFirstCall: this.hasCompletedFirstCall(),
      shouldShowGuide: this.shouldShowGuide()
    };
  }

  /**
   * Reset all first-time user data (for testing or reset)
   */
  resetFirstTimeStatus() {
    try {
      localStorage.removeItem(this.storageKey);
      localStorage.removeItem(this.guideCompletedKey);
      localStorage.removeItem(this.firstCallKey);
      console.log('🔄 First-time user status reset');
    } catch (error) {
      console.error('Error resetting first-time status:', error);
    }
  }

  /**
   * Get helpful tips based on user progress
   * @returns {Array}
   */
  getContextualTips() {
    const progress = this.getOnboardingProgress();
    const tips = [];

    if (!progress.hasCompletedGuide) {
      tips.push({
        type: 'guide',
        message: 'Complete the getting started guide for the best experience',
        action: 'Show Guide'
      });
    }

    if (!progress.hasCompletedFirstCall) {
      tips.push({
        type: 'first-call',
        message: 'Ready for your first conversation with Preptune?',
        action: 'Start Call'
      });
    }

    return tips;
  }

  /**
   * Track user interaction milestones
   * @param {string} milestone - The milestone achieved
   */
  trackMilestone(milestone) {
    try {
      const milestoneKey = `preptune-milestone-${milestone}`;
      const timestamp = new Date().toISOString();
      
      localStorage.setItem(milestoneKey, timestamp);
      console.log(`🎯 Milestone tracked: ${milestone} at ${timestamp}`);
    } catch (error) {
      console.error(`Error tracking milestone ${milestone}:`, error);
    }
  }

  /**
   * Get all completed milestones
   * @returns {Object}
   */
  getCompletedMilestones() {
    const milestones = {};
    
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('preptune-milestone-')) {
          const milestone = key.replace('preptune-milestone-', '');
          milestones[milestone] = localStorage.getItem(key);
        }
      }
    } catch (error) {
      console.error('Error getting milestones:', error);
    }
    
    return milestones;
  }

  /**
   * Check if user needs encouragement (hasn't used app in a while)
   * @returns {boolean}
   */
  needsEncouragement() {
    try {
      const lastCallKey = 'preptune-last-call-time';
      const lastCall = localStorage.getItem(lastCallKey);
      
      if (!lastCall) return false;
      
      const lastCallTime = new Date(lastCall);
      const now = new Date();
      const daysSinceLastCall = (now - lastCallTime) / (1000 * 60 * 60 * 24);
      
      // Show encouragement if it's been more than 3 days
      return daysSinceLastCall > 3;
    } catch (error) {
      console.error('Error checking encouragement status:', error);
      return false;
    }
  }

  /**
   * Update last call time
   */
  updateLastCallTime() {
    try {
      const lastCallKey = 'preptune-last-call-time';
      localStorage.setItem(lastCallKey, new Date().toISOString());
    } catch (error) {
      console.error('Error updating last call time:', error);
    }
  }

  /**
   * Get personalized welcome message based on user status
   * @returns {string}
   */
  getWelcomeMessage() {
    const progress = this.getOnboardingProgress();
    
    if (progress.isFirstTime) {
      return "Welcome to Preptune! Let's get you started with your AI companion.";
    } else if (!progress.hasCompletedFirstCall) {
      return "Welcome back! Ready for your first conversation?";
    } else if (this.needsEncouragement()) {
      return "Good to see you again! Your AI companion is ready to chat.";
    } else {
      return "Welcome back to Preptune!";
    }
  }
}

export default FirstTimeUserService;